import { Box, Image, VStack } from '@chakra-ui/react';
import Markdown from '@/components/atoms/Markdown/Markdown';
import { POST_BODY_MAX_FONT_SIZE, POST_BODY_MIN_FONT_SIZE } from '@/constants/contentFeed';
import useFitText from '@/hooks/useFitText';
import { PORTRAIT_RATIO } from '@/modules/content/common/videoOrientation';
import LoopStage from '@/modules/content/loop/parts/LoopStage';
import LoopTitle from '@/modules/content/loop/parts/LoopTitle';
import CreatorIdentity from '@/modules/content/loop/parts/CreatorIdentity';
import PublishedMetaRow from '@/modules/content/loop/parts/PublishedMetaRow';
import { PORTRAIT_FRAME_SIZING } from '@/modules/content/loop/model/loopLayout';
import { readDetails } from '@/modules/content/loop/model/contentDetails';
import { TLoopVariantProps } from '@/modules/content/loop/model/types';

const POST_GRADIENT = 'linear-gradient(175deg, #6110D9 0%, #7A1BC8 42%, #C2542F 84%, #E8672F 100%)';

const CLIP_FADE = 'linear-gradient(to bottom, black 88%, transparent 100%)';

// Post — rendered inline as the styled gradient card, in the same frame as a portrait
// reel so the loop does not resize between content types.
const PostVariant = ({ content, onNotInterested, onReport, rail, nav }: TLoopVariantProps) => {
  const details = readDetails(content);
  const image = content.effectiveThumbnailUrl ?? content.thumbnailUrl;

  // Re-fit whenever the body changes — advancing the loop reuses this component, so the
  // next post must be measured rather than inheriting the previous one's size.
  const bodyFit = useFitText(details.body, {
    minFontSize: POST_BODY_MIN_FONT_SIZE,
    maxFontSize: POST_BODY_MAX_FONT_SIZE,
  });

  return (
    <LoopStage
      center={
        <VStack
          {...PORTRAIT_FRAME_SIZING}
          align="stretch"
          gap="14px"
          padding="20px"
          borderRadius="20px"
          overflow="hidden"
          background={POST_GRADIENT}
          sx={{ aspectRatio: String(PORTRAIT_RATIO) }}
        >
          <CreatorIdentity content={content} onNotInterested={onNotInterested} onReport={onReport} isOnDark />
          {/* On the post card the title sits above the body as a kicker, so it is set
              smaller than the body rather than larger. */}
          <LoopTitle isOnDark fontSize="15px">
            {content.title || 'Untitled'}
          </LoopTitle>

          {/* The card is a fixed frame, so the body absorbs the leftover height and the
              image stays pinned to the bottom. The text is scaled to fill that height: a
              short post reads large, a long one steps down until it fits. Only a post too
              long even at the minimum size gets clipped, and that cut is faded rather than
              sliced mid-line. It never scrolls — an inner scroll area would fight the
              loop's own swipe navigation. */}
          {details.body && (
            <Box
              ref={bodyFit.containerRef}
              flex="1 1 auto"
              minHeight={0}
              overflow="hidden"
              sx={bodyFit.isOverflowing ? { maskImage: CLIP_FADE, WebkitMaskImage: CLIP_FADE } : undefined}
            >
              {/* The fitted size is set here and everything below inherits it — including
                  the Markdown wrapper, whose theme otherwise pins an explicit color and
                  size that no parent style can cascade past. Spacing is in `em` and
                  line-height unitless so both scale with the fitted size. */}
              <Box
                ref={bodyFit.contentRef}
                color="white"
                fontWeight={600}
                fontSize={`${bodyFit.fontSize}px`}
                lineHeight={1.22}
              >
                <Markdown
                  color="inherit"
                  fontSize="inherit"
                  fontWeight="inherit"
                  lineHeight="inherit"
                  sx={{
                    // Chakra's Text ships `defaultProps.color` and a fixed `lineHeight`,
                    // so every child would otherwise render grey at a constant leading —
                    // ignoring the fitted size entirely. These descendant rules outrank
                    // the per-element defaults on specificity and restore inheritance.
                    '& p, & li, & a, & h1, & h2, & h3': {
                      color: 'inherit',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      lineHeight: 'inherit',
                    },
                    '& p': { marginBottom: '0.5em' },
                    // The theme's link color is near-invisible on this gradient, so links
                    // stay white and rely on an underline.
                    '& a': { textDecoration: 'underline' },
                  }}
                >
                  {details.body}
                </Markdown>
              </Box>
              <PublishedMetaRow content={content} isOnDark />
            </Box>
          )}

          {image && (
            <Image
              src={image}
              alt=""
              width="100%"
              flexShrink={0}
              maxHeight="42%"
              borderRadius="12px"
              objectFit="cover"
            />
          )}
        </VStack>
      }
      right={
        <>
          {rail}
          {nav}
        </>
      }
    />
  );
};

export default PostVariant;
